<template>
  <div>
    <b-container>
      <div v-if="isGlobalApp" class="d-flex justify-content-center mt-2 mb-2">
        <b-img width="150" height="130" :src="require('@base/assets/images/flealover-sitelogo.png')" />
      </div>
      <div v-else class="d-flex justify-content-center">
        <b-img :src="require('@base/assets/images/sitelogo.png')" />
      </div>
      <b-row v-if="anonMarket.visualName" class="justify-content-center">
        <h4 class="w-100 text-center bg-dark text-white p-2 rounded">{{ anonMarket.visualName }}</h4>
      </b-row>
      <b-row class="mt-3 justify-content-between">
        <!-- Download App Card -->
        <b-col
          v-if="anonMarket.mobileAppAccess"
          cols="12"
          :md="anonMarket.gatewayApiValid && anonMarket.onlineBookingFeature && anonMarket.customerAccessToBooking && anonMarket.showPaymentTerms ? 6 : 12"
          class="mb-3 d-flex align-items-stretch">
          <b-card
            class="flex-fill d-flex flex-column"
            header-class="text-center"
          >
            <template v-slot:header>
              <h4 class="mb-0">
                {{ $t('pages.login.user.download_app_title_post_overview') }}
              </h4>
            </template>
            <b-card-body class="card-body--tight">
              <!-- badges -->
              <div class="badges">
                <a
                  :href="appStoreUrl"
                  target="_blank"
                >
                  <b-img
                    :src="require(`@base/assets/images/badges/download-on-the-app-store-${$_languageCode}.svg`)"
                    width="180"
                    height="60"
                    style="width: 180px; height: 60px; max-width: none;"
                    alt="App Store"
                  />
                </a>
                <a
                  :href="playMarketUrl"
                  target="_blank"
                >
                  <b-img
                    :src="require(`@base/assets/images/badges/google-play-badge-${$_languageCode}.png`)"
                    width="180"
                    height="60"
                    style="width: 180px; height: 60px; max-width: none;"
                    alt="Google Play"
                  />
                </a>
              </div>

              <!-- help-text -->
              <div class="help-text">
                <h5
                  v-html="$t('pages.login.user.download_app_help_text', {
                    app_help_link: mobileAppHelpUrl
                  })"
                />
              </div>
            </b-card-body>
          </b-card>
        </b-col>

        <!-- Book Now Card -->
        <b-col
          v-if="anonMarket.gatewayApiValid && anonMarket.onlineBookingFeature && anonMarket.customerAccessToBooking && anonMarket.showPaymentTerms"
          cols="12"
          :md="anonMarket.mobileAppAccess ? 6 : 12"
          class="mb-3 d-flex align-items-stretch">
          <b-card
            class="flex-fill d-flex flex-column"
            header-class="text-center"
          >
            <template v-slot:header>
              <h4 class="mb-0">
                {{ $t('pages.login.user.book_now_title') }}
              </h4>
            </template>
            <b-card-body class="card-body--tight">
              <!-- Booking button -->
              <div class="booking-wrap">
                <b-button
                  variant="success"
                  class="booking-btn"
                  @click="$router.push({ name: 'create-booking' })"
                >
                  {{ $t('navigation.customer.create_new_booking') }}
                </b-button>
              </div>

              <!-- Card logos -->
              <div class="card-logos">
                <b-img
                  :src="require('@base/assets/images/svg/visa-logo.svg')"
                  width="70" height="35"
                  alt="Visa"
                />
                <b-img
                  :src="require('@base/assets/images/svg/mastercard-logo.svg')"
                  width="70" height="35"
                  alt="Mastercard"
                />
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <b-row v-if="!anonMarket.mobileAppAccess">
        <b-col cols="12" md="12" sm="12" lg="12">
          <h5
            class="pb-2 ml-3"
            v-html="$t('pages.login.user.download_app_help_text', {
              app_help_link: mobileAppHelpUrl
            })"
          ></h5>
        </b-col>
      </b-row>

      <b-row class="mt-3">
        <b-col
          class="user-login-container mr-3 mt-2"
        >
          <component v-bind:is="componentName" />
        </b-col>
        <b-col
          v-if="anonMarket && anonMarket.marketImages.length"
          class="mt-2 no-padding-left no-padding-right custom-container-md"
          cols="12" md="12" sm="12" lg="8"
        >
          <image-slider
            :images="anonMarket.marketImages"
          />
        </b-col>
      </b-row>
      <b-row class="justify-content-center">
        <b-card-group deck class="mt-2">
          <b-col
            class="no-padding-right"
            cols="12" md="12" sm="12" lg="7"
            v-if="anonMarket.description || anonMarket.extraMarketInfo"
          >
            <market-description-card
              :market="anonMarket"
            />
          </b-col>
          <b-col
            class="no-padding-right"
          >
            <market-contact-info-card
              :market="anonMarket"
              :market-feedback="marketFeedback"
            />
            <market-opening-hours-card
              :market="anonMarket"
            />
          </b-col>
        </b-card-group>
      </b-row>
    </b-container>
  </div>
</template>

<script>
  import UserLogin from '@anonymous/components/user/UserLogin.vue'
  import UserResetPassword from '@anonymous/components/user/UserResetPassword.vue'
  import UserConfirmResetPassword from '@anonymous/components/user/UserConfirmResetPassword.vue'
  import UserEmailConfirm from '@anonymous/components/user/UserEmailConfirm.vue'
  import ImageSlider from '@base/components/ImageSlider.vue'
  import MarketDescriptionCard from '@base/components/MarketDescriptionCard.vue'
  import MarketContactInfoCard from '@base/components/MarketContactInfoCard.vue'
  import MarketOpeningHoursCard from '@base/components/MarketOpeningHoursCard.vue'
  import { mapState } from 'vuex'
  import { anonRequestInterceptor } from '@base/store'
  import { marketFeedback } from '@base/services'
  import { APP_STORE_URL, PLAY_MARKET_URL, MOBILE_APP_HELP_URLS, IS_GLOBAL_APP } from '@base/configs'


  marketFeedback.interceptors.request.use(anonRequestInterceptor)

  export default {
    name: 'AnonUserView',

    components: {
      UserLogin,
      UserResetPassword,
      UserConfirmResetPassword,
      UserEmailConfirm,
      ImageSlider,
      MarketDescriptionCard,
      MarketContactInfoCard,
      MarketOpeningHoursCard,
    },

    data () {
      return {
        marketFeedback: marketFeedback,
        appStoreUrl: APP_STORE_URL,
        playMarketUrl: PLAY_MARKET_URL,
        isGlobalApp: IS_GLOBAL_APP,
      }
    },

    created() {
      window.addEventListener('resize', this.handleResize)
    },

    computed: {
      ...mapState(['anonMarket']),

      mobileAppHelpUrl() {
        return MOBILE_APP_HELP_URLS[this.$i18n.locale.toUpperCase()] || MOBILE_APP_HELP_URLS.EN
      },
    },

    props: {
      componentName: {
        type: String,
        required: true,
      },
    },

    mounted() {
      this.setCardFooterStyle()
    },

    methods: {
      setCardFooterStyle () {

        // Define max height of all card footers
        const cardFooterClass = document.getElementsByClassName('card-footer')
        const customCardFooterClass = document.getElementsByClassName('custom-card-footer')
        let maxFooterHeight = 0
        for (const elem of customCardFooterClass) {
          if (elem.offsetHeight > maxFooterHeight) {
            maxFooterHeight = elem.offsetHeight
          }
        }

        // Set max height to every footer of card to make it symmetrical
        // and center footer content
        for (const elem of cardFooterClass) {
          elem.style.height = `calc(${maxFooterHeight}px + 1rem)`
          elem.style.display = 'flex'
          elem.style.justifyContent = 'center'
          elem.style.alignItems = 'center'
        }
      },

      handleResize () {
        this.setCardFooterStyle()
      },
    }
  }
</script>

<style scoped>

  .b-card {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .card-body--tight {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding: 1rem !important;
  }

  .badges {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 2rem;
  }

  @media (max-width: 991px) {
    .badges {
      flex-direction: column;
      gap: 0.5rem;
    }
    .badges a {
      margin: 0 auto;
    }
  }

  .help-text {
    margin-top: 0.5rem;
    margin-bottom: 0.75rem;
    text-align: center;
    font-size: 0.95rem;
    color: #555;
  }

  .booking-wrap {
    margin-top: 1.03rem;
  }

  .booking-btn {
    padding: 1.15rem 2rem !important;
    font-size: 1.2rem !important;
    font-weight: 600 !important;
    border-radius: 6px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06) !important;
  }

  .card-logos {
    display: flex;
    gap: 1rem;
    margin-top: 0.85rem;
    justify-content: center;
  }

  @media (max-width: 767px) {
    .help-text,
    .booking-wrap,
    .card-logos {
      margin: 0 auto 0.75rem !important;
    }
  }

</style>
